import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CHURCH } from "@/data/church";

const LINKS = [
  { label: "Home", to: "/", testId: "nav-link-home" },
  { label: "Posters & Events", to: "/events", testId: "nav-link-events" },
  { label: "News", to: "/news", testId: "nav-link-news" },
  { label: "Contact", to: "/contact", testId: "nav-link-contact" },
];

const SCALES = [
  { key: "sm", label: "A", size: "100%", testId: "text-size-normal-button" },
  { key: "md", label: "A+", size: "112.5%", testId: "text-size-large-button" },
  { key: "lg", label: "A++", size: "125%", testId: "text-size-largest-button" },
];

const TextSizeControl = () => {
  const [scale, setScale] = useState("sm");

  useEffect(() => {
    const saved = localStorage.getItem("ssc-text-scale") || "sm";
    const s = SCALES.find((x) => x.key === saved) || SCALES[0];
    document.documentElement.style.fontSize = s.size;
    setScale(s.key);
  }, []);

  const apply = (key) => {
    const s = SCALES.find((x) => x.key === key);
    document.documentElement.style.fontSize = s.size;
    localStorage.setItem("ssc-text-scale", key);
    setScale(key);
  };

  return (
    <div
      data-testid="text-size-control"
      className="flex items-center rounded-full border border-[#1A222D]/15 bg-white/70 p-1"
      role="group"
      aria-label="Adjust text size"
    >
      {SCALES.map((s) => (
        <button
          key={s.key}
          data-testid={s.testId}
          onClick={() => apply(s.key)}
          aria-pressed={scale === s.key}
          title={`Text size ${s.label}`}
          className={`min-h-10 min-w-10 rounded-full px-2 text-sm font-semibold transition-colors duration-200 ${
            scale === s.key
              ? "bg-[#6B21A8] text-[#FBF9F5]"
              : "text-[#3D4958] hover:bg-[#F3EFEA]"
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-[#C59B4B]/20 bg-[#FBF9F5]/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-brand-link" className="group flex flex-col">
          <span className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-[#101826] leading-tight">
            Stafford Spiritualist Church
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Main navigation">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `relative py-2 text-base font-medium transition-colors duration-200 after:absolute after:left-0 after:-bottom-0.5 after:h-0.5 after:bg-[#C59B4B] after:transition-all after:duration-300 ${
                  isActive
                    ? "text-[#7C2DAE] after:w-full"
                    : "text-[#3D4958] hover:text-[#6B21A8] after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <TextSizeControl />
          </div>
          <a
            href={CHURCH.phoneHref}
            data-testid="nav-call-button"
            className="hidden sm:inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#6B21A8] px-5 text-base font-semibold text-[#FBF9F5] shadow-md transition-all duration-200 hover:bg-[#7C2DAE] hover:shadow-lg active:scale-95"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {CHURCH.phoneDisplay}
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            className="lg:hidden inline-flex min-h-12 min-w-12 items-center justify-center rounded-xl border border-[#1A222D]/15 bg-white/80 text-[#101826]"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#C59B4B]/20 bg-[#FBF9F5] px-5 pb-6 pt-3"
        >
          <nav className="flex flex-col" aria-label="Mobile navigation">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testId}`}
                  className={({ isActive }) =>
                  `border-b border-[#1A222D]/8 py-4 text-lg font-medium ${
                    isActive ? "text-[#7C2DAE]" : "text-[#1A222D]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-5 flex flex-col gap-4">
            <a
              href={CHURCH.phoneHref}
              data-testid="mobile-call-button"
              className="inline-flex min-h-14 items-center justify-center gap-2 rounded-xl bg-[#6B21A8] px-5 text-lg font-semibold text-[#FBF9F5] shadow-md"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              Call {CHURCH.phoneDisplay}
            </a>
            <div className="flex justify-center">
              <TextSizeControl />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
